<template>
  <div>
    <h2 v-if="heading" class="mb-2 subtitle-1 <lg:mt-6">
      {{ heading }}
    </h2>
    <p v-if="description" class="body-3 mb-4 lg:mb-6" :class="{ '<lg:mt-6': !heading }">
      {{ description }}
    </p>
    <vf-carousel
      v-if="productFeatures && showProductFeatures"
      class="mb-4 lg:mb-6"
      class-container="gap-1 <lg:scroll-px-4 <lg:px-4 <lg:-mx-4 lg:grid lg:cols-4"
      class-controls="!hidden"
    >
      <button
        v-for="({ bgImageURL, label, thumbnail }, i) in productFeatures.data.slice(0, 4)"
        :key="i"
        v-style:w="{ sm: '12.75rem', lg: '100%' }"
        class="relative aspect-square f-col center gap-3 p-2 c-white transition"
        :style="{
          'background': `url(${getImageTransformations(bgImageURL, { excludeDPR: true,
                                                                     quality: 'auto',
                                                                     width: 768,
                                                                     sharpen: 0 })})`,
          'background-size': 'cover',
        }"

        @click="PanelProductDetailCard.open({
          data: productFeatures.data,
          title: productFeatures.label || $t.features,
        })"
      >
        <span class="absolute-0" style="background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.5))" />
        <vf-icon v-if="thumbnail" size="5xl" :src="thumbnail" />
        <span v-if="label" class="relative text-center subtitle-4">
          {{ label }}
        </span>
      </button>
    </vf-carousel>
    <vf-accordion
      v-for="({ data, id, label, layout = 'grid', expanded }, i) in
        showProductFeatures ? productDetails.filter(({ id }) => id !== 'productFeatures') : productDetails"
      :key="i"
      :id
      :class="{ 'b-b': i === productDetails.length - 1 }"
      :open="$feature.allowOpenedDetailsAccordionsOnPDP ? expanded : i === 0"
      :title="label"
      variant="aligned"
    >
      <div class="body-3 pb-6">
        <component
          :is="componentTypes[DETAIL_LAYOUTS.includes(layout) ? layout : 'detailCard']"
          :data
          :title="label"
        />
      </div>
    </vf-accordion>
    <panel-product-detail-card size="sm" />
  </div>
</template>

<script setup lang="ts">
import type { ProductDetails } from '#root/api/clients/product/data-contracts'
import {
  LazyProductAccordionsBulletin,
  LazyProductAccordionsDetailCard,
  LazyProductAccordionsGrid,
  LazyProductAccordionsKeyValue,
  LazyProductAccordionsScale
} from '#components'
import { DETAIL_LAYOUTS } from '#types/product'

const props = defineProps<{
  productDetails: ProductDetails[]
  heading?: string
  description?: string
}>()

const componentTypes = {
  bulletin: LazyProductAccordionsBulletin,
  detailCard: LazyProductAccordionsDetailCard,
  grid: LazyProductAccordionsGrid,
  keyValue: LazyProductAccordionsKeyValue,
  modalGrid: LazyProductAccordionsDetailCard,
  scale: LazyProductAccordionsScale
}

const productFeatures = computed(() => props.productDetails.find(({ id }) => id === 'productFeatures'))
const showProductFeatures = computed(() => productFeatures.value
  && productFeatures.value.data.length > 1
  && productFeatures.value.data.some(({ bgImageURL }) => !!bgImageURL)
)

const PanelProductDetailCard = createPanel('product-detail-card')
</script>
